import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { Edit2, Save, X } from 'lucide-react';
import { FormSection, Input, Textarea, Button } from '../../common/ui';

const CustomerInformation = forwardRef(({ 
  repairData, 
  readOnly = true, 
  onSave,
  showLinks = true,
  onNotifyCustomerChange,
  externalEditMode
}, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    customer_name: repairData.customer_name || '',
    customer_email: repairData.customer_email || '',
    customer_phone: repairData.customer_phone || '',
    customer_return_address: repairData.customer_return_address || '',
    help_scout_link: repairData.help_scout_link || '',
    woocommerce_link: repairData.woocommerce_link || '',
    notify_customer: repairData.notify_customer !== undefined ? repairData.notify_customer : true
  });
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState('');

  const effectiveEditing = externalEditMode === 'edit' || isEditing;

  // Sync local editing state with external edit mode
  useEffect(() => {
    if (externalEditMode === 'edit' && !isEditing) {
      setEditedData({
        customer_name: repairData.customer_name || '',
        customer_email: repairData.customer_email || '',
        customer_phone: repairData.customer_phone || '',
        customer_return_address: repairData.customer_return_address || '',
        help_scout_link: repairData.help_scout_link || '',
        woocommerce_link: urlsToOrderIds(repairData.woocommerce_link || ''),
        notify_customer: repairData.notify_customer !== undefined ? repairData.notify_customer : true
      });
      setIsEditing(true);
    } else if (externalEditMode !== 'edit' && isEditing) {
      setIsEditing(false);
    }
  }, [externalEditMode]);

  useImperativeHandle(ref, () => ({
    getEditedData: () => ({
      ...editedData,
      woocommerce_link: parseWooCommerceInput(editedData.woocommerce_link)
    }),
    hasChanges: () => {
      const orig = {
        customer_name: (repairData.customer_name || '').trim(),
        customer_email: (repairData.customer_email || '').trim(),
        customer_phone: (repairData.customer_phone || '').trim(),
        customer_return_address: (repairData.customer_return_address || '').trim(),
        help_scout_link: (repairData.help_scout_link || '').trim(),
        woocommerce_link: (repairData.woocommerce_link || '').trim(),
        notify_customer: repairData.notify_customer !== undefined ? repairData.notify_customer : true
      };
      const curr = {
        customer_name: (editedData.customer_name || '').trim(),
        customer_email: (editedData.customer_email || '').trim(),
        customer_phone: (editedData.customer_phone || '').trim(),
        customer_return_address: (editedData.customer_return_address || '').trim(),
        help_scout_link: (editedData.help_scout_link || '').trim(),
        woocommerce_link: parseWooCommerceInput(editedData.woocommerce_link || '').trim(),
        notify_customer: editedData.notify_customer
      };
      return JSON.stringify(orig) !== JSON.stringify(curr);
    },
    validate: () => {
      if (!editedData.customer_name || !editedData.customer_name.trim()) {
        return { valid: false, error: 'Customer name is required' };
      }
      if (editedData.customer_email && editedData.customer_email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editedData.customer_email)) {
          return { valid: false, error: 'Please enter a valid email address' };
        }
      }
      return { valid: true, error: null };
    },
    reset: () => {
      setEditedData({
        customer_name: repairData.customer_name || '',
        customer_email: repairData.customer_email || '',
        customer_phone: repairData.customer_phone || '',
        customer_return_address: repairData.customer_return_address || '',
        help_scout_link: repairData.help_scout_link || '',
        woocommerce_link: repairData.woocommerce_link || '',
        notify_customer: repairData.notify_customer !== undefined ? repairData.notify_customer : true
      });
      setValidationError('');
      setIsEditing(false);
    }
  }));

  // Update internal state when repairData prop changes (e.g., after save/refresh)
  useEffect(() => {
    if (!effectiveEditing) {
      setEditedData({
        customer_name: repairData.customer_name || '',
        customer_email: repairData.customer_email || '',
        customer_phone: repairData.customer_phone || '',
        customer_return_address: repairData.customer_return_address || '',
        help_scout_link: repairData.help_scout_link || '',
        woocommerce_link: repairData.woocommerce_link || '',
        notify_customer: repairData.notify_customer !== undefined ? repairData.notify_customer : true
      });
    }
  }, [
    repairData.customer_name,
    repairData.customer_email,
    repairData.customer_phone,
    repairData.customer_return_address,
    repairData.help_scout_link,
    repairData.woocommerce_link,
    repairData.notify_customer,
    effectiveEditing
  ]);

  // Auto-open edit mode when status is waiting_for_payment (standalone only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!externalEditMode && repairData.status === 'waiting_for_payment' && !readOnly) {
      setIsEditing(true);
    }
  }, [repairData.status, readOnly]);

  const handleEdit = () => {
    setEditedData({
      customer_name: repairData.customer_name || '',
      customer_email: repairData.customer_email || '',
      customer_phone: repairData.customer_phone || '',
      customer_return_address: repairData.customer_return_address || '',
      help_scout_link: repairData.help_scout_link || '',
      // Convert stored URLs to order IDs for easier editing
      woocommerce_link: urlsToOrderIds(repairData.woocommerce_link || ''),
      notify_customer: repairData.notify_customer !== undefined ? repairData.notify_customer : true
    });
    setValidationError('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedData({
      customer_name: repairData.customer_name || '',
      customer_email: repairData.customer_email || '',
      customer_phone: repairData.customer_phone || '',
      customer_return_address: repairData.customer_return_address || '',
      help_scout_link: repairData.help_scout_link || '',
      woocommerce_link: repairData.woocommerce_link || '',
      notify_customer: repairData.notify_customer !== undefined ? repairData.notify_customer : true
    });
    setValidationError('');
    setIsEditing(false);
  };

  const handleSave = async () => {
    // Customer name is required
    if (!editedData.customer_name || !editedData.customer_name.trim()) {
      setValidationError('Customer name is required');
      return;
    }

    // Validate email format if provided
    if (editedData.customer_email && editedData.customer_email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editedData.customer_email)) {
        setValidationError('Please enter a valid email address');
        return;
      }
    }

    if (onSave) {
      setIsSaving(true);
      setValidationError('');
      try {
        // Convert WooCommerce order IDs to full URLs before saving
        const dataToSave = {
          ...editedData,
          woocommerce_link: parseWooCommerceInput(editedData.woocommerce_link),
          notify_customer: editedData.notify_customer
        };
        await onSave(dataToSave);
        setIsEditing(false);
      } catch (error) {
        console.error('Error saving customer information:', error);
        setValidationError('Failed to save changes');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  const formatReturnAddress = (address) => {
    if (!address) return 'Not provided';
    // Replace newlines with commas
    return address.replace(/\n/g, ', ');
  };

  const extractHelpScoutId = (url) => {
    if (!url) return null;
    // Remove query parameters (everything after ?)
    const urlWithoutQuery = url.split('?')[0];
    // Extract the conversation ID (the number after the last "/")
    const parts = urlWithoutQuery.split('/');
    const lastPart = parts[parts.length - 1];
    // Check if it's a number
    if (lastPart && /^\d+$/.test(lastPart)) {
      return lastPart;
    }
    return null;
  };

  const extractWooCommerceId = (url) => {
    if (!url) return null;
    try {
      // Parse URL to extract query parameters
      const urlObj = new URL(url);
      const postId = urlObj.searchParams.get('post');
      // Check if it's a number
      if (postId && /^\d+$/.test(postId)) {
        return postId;
      }
      // HPOS format: admin.php?page=wc-orders&action=edit&id=123
      if (urlObj.searchParams.get('page') === 'wc-orders') {
        const hposId = urlObj.searchParams.get('id');
        if (hposId && /^\d+$/.test(hposId)) {
          return hposId;
        }
      }
    } catch (e) {
      // Invalid URL, return null
      return null;
    }
    return null;
  };

  // Parse multiple WooCommerce links from comma-separated string
  const parseWooCommerceLinks = (linksString) => {
    if (!linksString) return [];
    return linksString.split(',').map(link => link.trim()).filter(Boolean);
  };

  // Convert a single order ID or URL to a full WooCommerce URL
  const toWooCommerceUrl = (input) => {
    let trimmed = input.trim();
    if (!trimmed) return null;
    
    // Strip leading # if present (e.g., "#148000" -> "148000")
    if (trimmed.startsWith('#')) {
      trimmed = trimmed.slice(1);
    }
    
    // If it's just a number, convert to URL
    if (/^\d+$/.test(trimmed)) {
      return `https://b2b.naviter.com/wp-admin/admin.php?page=wc-orders&action=edit&id=${trimmed}`;
    }
    
    // If it's already a URL, return as-is
    if (trimmed.startsWith('http')) {
      return trimmed;
    }
    
    return null;
  };

  // Parse input with multiple order IDs/URLs separated by various delimiters
  // Returns comma-separated full URLs for storage
  const parseWooCommerceInput = (input) => {
    if (!input) return '';
    
    // Split by common delimiters: comma, semicolon, space, newline
    const parts = input.split(/[,;\s\n]+/).filter(Boolean);
    
    // Convert each part to a full URL
    const urls = parts
      .map(part => toWooCommerceUrl(part))
      .filter(Boolean);
    
    // Join with commas for storage
    return urls.join(',');
  };

  // Convert stored URLs back to just order IDs for easier editing
  // e.g., "https://...post=146086...,https://...post=146087..." -> "146086 146087"
  const urlsToOrderIds = (urlsString) => {
    if (!urlsString) return '';
    const urls = urlsString.split(',').map(u => u.trim()).filter(Boolean);
    const ids = urls.map(url => {
      const id = extractWooCommerceId(url);
      return id || url; // fallback to full URL if can't extract
    });
    return ids.join(' ');
  };

  // Live b2b status per linked order id, e.g. { "156004": { status: "repairs", label: "Repairs" } }.
  // Best-effort: a failed lookup just leaves the badge out.
  const [orderStatuses, setOrderStatuses] = useState({});
  const linkedOrderIds = showLinks
    ? parseWooCommerceLinks(repairData.woocommerce_link || '').map(extractWooCommerceId).filter(Boolean)
    : [];
  const linkedOrderIdsKey = linkedOrderIds.join(',');
  // Re-fetch when the linked orders change AND when the repair status changes:
  // a status move (e.g. → Backlog) can push the b2b order to Processing server-side,
  // and RepairView reloads the repair right after, so the badge follows without a page refresh.
  const repairStatus = repairData.status;
  useEffect(() => {
    if (!linkedOrderIdsKey) {
      setOrderStatuses({});
      return undefined;
    }
    let cancelled = false;
    axios.get('/api/b2b/orders/live-status', { params: { ids: linkedOrderIdsKey } })
      .then((res) => { if (!cancelled) setOrderStatuses((res.data && res.data.orders) || {}); })
      .catch(() => { if (!cancelled) setOrderStatuses({}); });
    return () => { cancelled = true; };
  }, [linkedOrderIdsKey, repairStatus]);

  const orderStatusBadgeClass = (slug) => {
    if (slug === 'processing') return 'bg-green-100 text-green-800';
    if (slug === 'repairs') return 'bg-amber-100 text-amber-800';
    return 'bg-gray-100 text-gray-600';
  };

  const renderActions = () => {
    if (readOnly || externalEditMode) return null;

    if (isEditing) {
      return (
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            variant="primary"
            size="xs"
            icon={Save}
            loading={isSaving}
          >
            Save
          </Button>
          <Button
            onClick={handleCancel}
            variant="ghost"
            size="xs"
            icon={X}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      );
    }

    return (
      <button
        onClick={handleEdit}
        className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
        title="Edit customer information"
      >
        <Edit2 className="h-4 w-4" />
      </button>
    );
  };

  return (
    <FormSection title="Customer Information" actions={renderActions()}>
      {validationError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{validationError}</p>
        </div>
      )}
      
      <div className="space-y-1.5">
        {effectiveEditing ? (
          <>
            {/* Edit Mode - Compact */}
            <div className="bg-gray-50/70 rounded p-2 space-y-1.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    size="sm"
                    value={editedData.customer_name}
                    onChange={(e) => handleChange('customer_name', e.target.value)}
                    placeholder="Enter customer name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Email
                  </label>
                  <Input
                    size="sm"
                    type="email"
                    value={editedData.customer_email}
                    onChange={(e) => handleChange('customer_email', e.target.value)}
                    placeholder="Enter customer email"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Phone
                  </label>
                  <Input
                    size="sm"
                    type="tel"
                    value={editedData.customer_phone}
                    onChange={(e) => handleChange('customer_phone', e.target.value)}
                    placeholder="Enter customer phone"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">
                  Return address
                </label>
                <Textarea
                  size="sm"
                  value={editedData.customer_return_address}
                  onChange={(e) => handleChange('customer_return_address', e.target.value)}
                  placeholder="Enter return address"
                  rows={2}
                />
              </div>
              
              {showLinks && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                      Help Scout link
                    </label>
                    <Input
                      size="sm"
                      type="url"
                      value={editedData.help_scout_link}
                      onChange={(e) => handleChange('help_scout_link', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                      WooCommerce link(s)
                    </label>
                    <Input
                      size="sm"
                      type="text"
                      value={editedData.woocommerce_link}
                      onChange={(e) => handleChange('woocommerce_link', e.target.value)}
                      placeholder="Order IDs (e.g., 146086 146087)"
                    />
                  </div>
                </div>
              )}
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="notify_customer_edit"
                  checked={editedData.notify_customer}
                  onChange={(e) => handleChange('notify_customer', e.target.checked)}
                  className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                />
                <label htmlFor="notify_customer_edit" className="text-xs font-medium text-gray-700">
                  Send email notifications to customer
                </label>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Read-Only Mode - Zebra rows */}
            <div className="rounded overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 py-1 px-2 bg-gray-50/70">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Name</span>
                  <span className="text-sm text-gray-900">{repairData.customer_name || 'Not provided'}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Phone</span>
                  <span className="text-sm text-gray-900">{repairData.customer_phone || '—'}</span>
                </div>
              </div>
              <div className="py-1 px-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Email</span>
                  <span className="text-sm text-gray-900 select-all">{repairData.customer_email || '—'}</span>
                  {repairData.customer_email && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${repairData.notify_customer !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {repairData.notify_customer !== false ? 'notifications on' : 'notifications off'}
                    </span>
                  )}
                </div>
              </div>
              {showLinks && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 py-1 px-2 bg-gray-50/70">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Help Scout</span>
                    {repairData.help_scout_link ? (
                      <a
                        href={repairData.help_scout_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                        title={repairData.help_scout_link}
                      >
                        #{extractHelpScoutId(repairData.help_scout_link) || 'Link'}
                      </a>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs uppercase tracking-wide font-medium text-gray-500">WooCommerce</span>
                    {repairData.woocommerce_link ? (
                      <span className="text-sm">
                        {parseWooCommerceLinks(repairData.woocommerce_link).map((link, index, arr) => (
                          <span key={index}>
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline"
                              title={link}
                            >
                              #{extractWooCommerceId(link) || 'Link'}
                            </a>
                            {orderStatuses[extractWooCommerceId(link)] && (
                              <span
                                className={`ml-1 inline-block rounded px-1.5 py-0.5 text-xs font-medium align-middle ${orderStatusBadgeClass(orderStatuses[extractWooCommerceId(link)].status)}`}
                                title={`B2B status: ${orderStatuses[extractWooCommerceId(link)].status}`}
                              >
                                {orderStatuses[extractWooCommerceId(link)].label}
                              </span>
                            )}
                            {index < arr.length - 1 && <span className="text-gray-400">, </span>}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            {repairData.customer_return_address && (
              <div className="mt-2 border-l-2 border-gray-200 pl-3">
                <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Return address</span>
                <p className="text-sm text-gray-900 whitespace-pre-wrap mt-0.5">{repairData.customer_return_address}</p>
              </div>
            )}
          </>
        )}
      </div>
    </FormSection>
  );
});

CustomerInformation.displayName = 'CustomerInformation';

export default CustomerInformation;
